<template>
    <div class="chart-container">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h5>
                <i class="fal fa-link text-primary"></i> Conexiones de Agentes
            </h5>
            <!-- <small class="text-muted" v-if="rangeText">{{ rangeText }}</small> -->
        </div>
        <div class="d-flex align-items-center mb-2">
            <label class="mb-0 mr-2">Desde</label>
            <input
                type="date"
                v-model="localFrom"
                class="form-control form-control-sm mr-2"
            />
            <label class="mb-0 mr-2">Hasta</label>
            <input
                type="date"
                v-model="localTo"
                class="form-control form-control-sm mr-2"
            />
            <button
                class="btn btn-primary btn-sm mr-2"
                @click="applyLocalFilter"
            >
                Aplicar
            </button>
            <button
                class="btn btn-secondary btn-sm"
                @click="clearLocalFilter"
                :disabled="!useLocalFilter && !localFrom && !localTo"
            >
                Limpiar
            </button>
        </div>
        <div class="table-responsive">
            <h6 class="mb-2">
                <i class="fal fa-list-alt text-info"></i> Resumen
            </h6>
            <table class="table table-hover mb-4">
                <thead>
                    <tr>
                        <th>Agente</th>
                        <th>Sesiones</th>
                        <th>Total Conectado</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in summaryRows" :key="row.id_usuario">
                        <td>{{ row.nombre }}</td>
                        <td>
                            <span class="badge badge-info">{{
                                row.sessions_count
                            }}</span>
                        </td>
                        <td>
                            <span class="time-badge">{{
                                formatSeconds(row.total_seconds)
                            }}</span>
                        </td>
                    </tr>
                    <tr v-if="!loading && summaryRows.length === 0">
                        <td colspan="3" class="text-center text-muted">
                            Sin datos
                        </td>
                    </tr>
                </tbody>
            </table>

            <h6 class="mb-2">
                <i class="fal fa-stream text-primary"></i> Detalle
            </h6>
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th>Agente</th>
                        <th>Login</th>
                        <th>Logout</th>
                        <th>Estado</th>
                        <th>IP</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.id">
                        <td>{{ row.nombre }}</td>
                        <td>{{ row.login_time || "-" }}</td>
                        <td>{{ row.logout_time || "-" }}</td>
                        <td>
                            <span :class="statusClass(row.status)">{{
                                displayStatus(row.status)
                            }}</span>
                        </td>
                        <td>
                            <code>{{ row.ip_address || "-" }}</code>
                        </td>
                    </tr>
                    <tr v-if="!loading && rows.length === 0">
                        <td colspan="5" class="text-center text-muted">
                            Sin datos
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="loading" class="text-center py-3">
            <i class="fa fa-spinner fa-spin"></i> Cargando...
        </div>
    </div>
</template>

<script>
export default {
    name: "AgentConnections",
    props: {
        period: { type: String, default: "mes" },
        from: { type: String, default: null },
        to: { type: String, default: null },
    },
    data() {
        return {
            rows: [],
            loading: false,
            rangeText: "",
            localFrom: null,
            localTo: null,
            useLocalFilter: false,
        };
    },
    watch: {
        period() {
            if (!this.useLocalFilter) this.loadData();
        },
        from(val) {
            if (!this.useLocalFilter) {
                this.localFrom = val;
                this.loadData();
            }
        },
        to(val) {
            if (!this.useLocalFilter) {
                this.localTo = val;
                this.loadData();
            }
        },
    },
    mounted() {
        // Inicializar filtros locales: si el padre trae fechas, usarlas; si no, hoy
        const today = new Date().toISOString().split("T")[0];
        if (this.from && this.to) {
            this.localFrom = this.from;
            this.localTo = this.to;
            this.useLocalFilter = true;
        } else {
            this.localFrom = today;
            this.localTo = today;
            this.useLocalFilter = true;
        }
        this.loadData();
    },
    computed: {
        summaryRows() {
            const map = new Map();
            this.rows.forEach((s) => {
                const key = s.id_usuario || s.nombre;
                if (!map.has(key)) {
                    map.set(key, {
                        id_usuario: s.id_usuario || key,
                        nombre: s.nombre,
                        sessions_count: 0,
                        total_seconds: 0,
                    });
                }
                const item = map.get(key);
                item.sessions_count += 1;
                item.total_seconds += this.durationSeconds(
                    s.login_time,
                    s.logout_time
                );
            });
            return Array.from(map.values());
        },
    },
    methods: {
        async loadData() {
            this.loading = true;
            try {
                const params = new URLSearchParams();
                const effFrom = this.useLocalFilter
                    ? this.localFrom
                    : this.from;
                const effTo = this.useLocalFilter ? this.localTo : this.to;
                if (effFrom && effTo) {
                    params.set("from", effFrom);
                    params.set("to", effTo);
                } else if (this.period) {
                    params.set("period", this.period);
                }
                const res = await fetch(
                    `/admin/agentes/connections-data?${params.toString()}`
                );
                const json = await res.json();
                if (json.success) {
                    this.rows = json.data || [];
                    this.rangeText = `${json.from} — ${json.to}`;
                } else {
                    this.rows = [];
                }
            } catch (e) {
                this.rows = [];
            } finally {
                this.loading = false;
            }
        },
        applyLocalFilter() {
            if (this.localFrom && this.localTo) {
                this.useLocalFilter = true;
                this.loadData();
            } else {
                // Si faltan fechas, no activar el filtro local
                this.useLocalFilter = false;
            }
        },
        clearLocalFilter() {
            const today = new Date().toISOString().split("T")[0];
            if (this.from && this.to) {
                this.localFrom = this.from;
                this.localTo = this.to;
            } else {
                this.localFrom = today;
                this.localTo = today;
            }
            this.useLocalFilter = true;
            this.loadData();
        },
        formatSeconds(total) {
            if (!total || total <= 0) return "00:00:00";
            const h = Math.floor(total / 3600)
                .toString()
                .padStart(2, "0");
            const m = Math.floor((total % 3600) / 60)
                .toString()
                .padStart(2, "0");
            const s = Math.floor(total % 60)
                .toString()
                .padStart(2, "0");
            return `${h}:${m}:${s}`;
        },
        statusClass(status) {
            const base = "badge";
            if (status === "active") return `${base} badge-info`;
            if (status === "ended") return `${base} badge-success`;
            if (status === "abandoned") return `${base} badge-warning`;
            return `${base} badge-secondary`;
        },
        displayStatus(status) {
            if (status === "active") return "Activa";
            if (status === "ended") return "Finalizada";
            if (status === "abandoned") return "Abandonada";
            return status || "-";
        },
        durationSeconds(login, logout) {
            if (!login) return 0;
            const toDate = (str) => {
                try {
                    return new Date((str || "").replace(" ", "T"));
                } catch (e) {
                    return null;
                }
            };
            const start = toDate(login);
            const end = logout ? toDate(logout) : new Date();
            if (
                !start ||
                !end ||
                isNaN(start.getTime()) ||
                isNaN(end.getTime())
            )
                return 0;
            const diff = Math.max(
                0,
                Math.floor((end.getTime() - start.getTime()) / 1000)
            );
            return diff;
        },
    },
};
</script>

<style scoped>
.time-badge {
    background-color: #17a2b8;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
}
</style>
